import React from 'react';
import StaticPageLayout from '../components/StaticPageLayout';

const RejectionAppealsPage = () => (
  <StaticPageLayout
    overline="Submission Policies"
    title="Rejection & Appeals"
    description="This page explains the grounds on which manuscripts may be rejected and outlines the formal process available to authors who wish to appeal an editorial decision."
    breadcrumbs={[{ label: 'Submission Policies', path: '/submission-process' }, { label: 'Rejection & Appeals', path: '/rejection-appeals' }]}
  >
    <div className="space-y-10">
      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-5 pb-3 border-b border-[#E5E7EB]">Grounds for Rejection</h2>
        <div className="space-y-2">
          {[
            { category: 'Scope', desc: 'The manuscript does not fall within the aims and scope of the selected journal.' },
            { category: 'Scientific Quality', desc: 'The research does not meet the minimum threshold for scientific rigour, originality, or methodological soundness.' },
            { category: 'Ethical Issues', desc: 'Evidence of plagiarism, data fabrication, undisclosed conflicts of interest, or other ethical violations.' },
            { category: 'Incomplete Submission', desc: 'The submission is missing required components or does not comply with formatting guidelines after reasonable notice.' },
            { category: 'Duplicate Submission', desc: 'The manuscript has been identified as under concurrent review at another publication.' },
            { category: 'Poor Presentation', desc: 'The manuscript is written to a standard that prevents effective editorial and peer review, with no scope for targeted revision.' },
          ].map(item => (
            <div key={item.category} className="flex gap-3 border border-[#E5E7EB] p-4 bg-white text-sm">
              <span className="font-semibold text-[#374151] flex-shrink-0 w-36">{item.category}</span>
              <span className="text-[#6B7280] leading-relaxed">{item.desc}</span>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">Appeals Process</h2>
        <p className="text-[#4B5563] leading-relaxed mb-4">
          Authors who believe their manuscript was rejected due to a procedural error, a significant misunderstanding of the research, or a demonstrably biased review may submit a formal appeal.
        </p>
        <div className="space-y-3">
          {[
            { step: '1', title: 'Submit a Written Appeal', desc: 'Email the editorial office within 30 days of the rejection decision, referencing your submission ID and providing a clear, evidence-based case for reconsideration.' },
            { step: '2', title: 'Editorial Review', desc: 'The editor-in-chief will review the appeal, the original manuscript, and the reviewer reports. This process typically takes 10–15 business days.' },
            { step: '3', title: 'Final Decision', desc: 'The editor-in-chief\'s decision on the appeal is final. If the appeal is upheld, the manuscript may be sent for additional review or directly reconsidered for publication.' },
          ].map(item => (
            <div key={item.step} className="flex gap-3 border border-[#E5E7EB] p-4 bg-white">
              <span className="w-6 h-6 bg-[#1A73E8] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{item.step}</span>
              <div>
                <p className="font-semibold text-[#1F1F1F] text-sm mb-0.5">{item.title}</p>
                <p className="text-sm text-[#6B7280] leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#F5F7FA] border border-[#E5E7EB] p-5">
        <p className="text-sm text-[#6B7280] leading-relaxed">
          <strong className="text-[#374151]">Note:</strong> Appeals based solely on disagreement with reviewer opinions, without new evidence or identification of procedural error, will not typically be considered. The appeals process is intended to ensure fairness in editorial proceedings, not to overturn legitimate expert scientific assessment.
        </p>
      </section>
    </div>
  </StaticPageLayout>
);

export default RejectionAppealsPage;
